import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Container, Button, Row, Col, Modal, ProgressBar, Form, Card, Spinner, Breadcrumb, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import ObjectPreviewModal from './ObjectPreviewModal';
import './ImageSlider.css';

const DirectoryThumbnail = memo(function DirectoryThumbnail({ dir, onNavigate }) {
  return (
    <div 
      className="file-thumbnail directory-thumbnail"
      style={{ 
        height: 180, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        cursor: 'pointer', 
        color: 'white'
      }} 
      onClick={() => onNavigate(dir.key)}
    >
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z" />
      </svg>
      <div style={{ fontSize: '14px', marginTop: '8px', fontWeight: 'bold' }}>
        📁 FOLDER
      </div>
      <div style={{ 
        fontSize: '12px', 
        opacity: 0.9, 
        textAlign: 'center', 
        padding: '0 8px', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap', 
        maxWidth: '100%',
        marginTop: '4px'
      }}>
        {dir.name}
      </div>
    </div>
  );
});

const ObjectThumbnail = memo(function ObjectThumbnail({ obj, token, onPreview }) {
  const API_BASE = 'https://r2-object-manager.vijay-vishwakarma.workers.dev';
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false); // Track if we've already loaded this image
  
  // Memoize file type checks to prevent unnecessary recalculations
  const { isImage, isVideo, isPdf, isDoc, isSpreadsheet, isPresentation, isArchive, isAudio, fileType } = useMemo(() => {
    const isImage = obj.key.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i);
    const isVideo = obj.key.match(/\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)$/i);
    const isPdf = obj.key.match(/\.pdf$/i);
    const isDoc = obj.key.match(/\.(doc|docx|txt|rtf)$/i);
    const isSpreadsheet = obj.key.match(/\.(xls|xlsx|csv)$/i);
    const isPresentation = obj.key.match(/\.(ppt|pptx)$/i);
    const isArchive = obj.key.match(/\.(zip|rar|7z|tar|gz)$/i);
    const isAudio = obj.key.match(/\.(mp3|wav|flac|aac|ogg)$/i);
    
    let fileType = 'file';
    if (isImage) fileType = 'image';
    else if (isVideo) fileType = 'video';
    else if (isPdf) fileType = 'pdf';
    else if (isDoc) fileType = 'document';
    else if (isSpreadsheet) fileType = 'spreadsheet';
    else if (isPresentation) fileType = 'presentation';
    else if (isArchive) fileType = 'archive';
    else if (isAudio) fileType = 'audio';
    
    return { isImage, isVideo, isPdf, isDoc, isSpreadsheet, isPresentation, isArchive, isAudio, fileType };
  }, [obj.key]);

  // Memoize the preview URL to prevent recreating it
  const previewUrl = useMemo(() => 
    `${API_BASE}/api/preview/${encodeURIComponent(obj.key)}`,
    [obj.key]
  );

  // Get file type specific icon and styling
  const getFileTypeDisplay = useMemo(() => {
    const getIcon = (type) => {
      switch(type) {
        case 'pdf':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#dc3545">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <text x="12" y="16" textAnchor="middle" fontSize="6" fill="white" fontWeight="bold">PDF</text>
            </svg>
          );
        case 'document':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#2b5797">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <path d="M8,12H16V13.5H8V12M8,14.5H13V16H8V14.5" fill="white"/>
            </svg>
          );
        case 'spreadsheet':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#0f7b0f">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <path d="M8,12H11V14H8V12M12,12H16V14H12V12M8,15H11V17H8V15M12,15H16V17H12V15" fill="white"/>
            </svg>
          );
        case 'presentation':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#d24726">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <circle cx="12" cy="14" r="2" fill="white"/>
              <path d="M9,12H15V16H9V12" fill="none" stroke="white" strokeWidth="0.5"/>
            </svg>
          );
        case 'archive':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#795548">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <path d="M8,12H16V16H8V12M10,13V15M12,13V15M14,13V15" fill="none" stroke="white" strokeWidth="1"/>
            </svg>
          );
        case 'audio':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#9c27b0">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              <circle cx="12" cy="14" r="3" fill="none" stroke="white" strokeWidth="1"/>
              <path d="M12,11V17M10,13H14" stroke="white" strokeWidth="1"/>
            </svg>
          );
        case 'video':
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
            </svg>
          );
        default:
          return (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#6c757d">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
          );
      }
    };

    const getBackground = (type) => {
      switch(type) {
        case 'pdf': return '#fff2f2';
        case 'document': return '#f0f4ff';
        case 'spreadsheet': return '#f0fff0';
        case 'presentation': return '#fff0ed';
        case 'archive': return '#f5f5f5';
        case 'audio': return '#fdf0ff';
        case 'video': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        default: return '#f8f9fa';
      }
    };

    const getLabel = (type) => {
      switch(type) {
        case 'pdf': return 'PDF';
        case 'document': return 'DOC';
        case 'spreadsheet': return 'XLS';
        case 'presentation': return 'PPT';
        case 'archive': return 'ZIP';
        case 'audio': return 'AUDIO';
        case 'video': return 'VIDEO';
        default: return 'FILE';
      }
    };

    return {
      icon: getIcon(fileType),
      background: getBackground(fileType),
      label: getLabel(fileType),
      textColor: fileType === 'video' ? 'white' : '#6c757d'
    };
  }, [fileType]);

  useEffect(() => {
    if (!isImage || hasLoaded) return; // Don't refetch if already loaded
    
    let isMounted = true;
    let cancelToken = axios.CancelToken.source();
    
    setLoading(true);
    setError(false);
    
    axios.get(previewUrl, {
      headers: { Authorization: 'Basic ' + token },
      responseType: 'blob',
      cancelToken: cancelToken.token
    }).then(res => {
      if (!isMounted) return;
      
      const reader = new FileReader();
      reader.onload = () => {
        if (isMounted) {
          setImageSrc(reader.result);
          setLoading(false);
          setHasLoaded(true); // Mark as loaded
        }
      };
      reader.onerror = () => {
        if (isMounted) {
          console.error('FileReader error for thumbnail', obj.key);
          setError(true);
          setLoading(false);
        }
      };
      reader.readAsDataURL(res.data);
    }).catch(error => {
      if (isMounted && !axios.isCancel(error)) {
        console.error('Thumbnail fetch error for', obj.key, error);
        setError(true);
        setLoading(false);
      }
    });
    
    return () => {
      isMounted = false;
      cancelToken.cancel('Component unmounted');
    };
  }, [previewUrl, token, isImage, hasLoaded]); // Added hasLoaded to dependencies

  if (isImage) {
    return loading ? (
      <div style={{height:180,display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Spinner animation="border" />
      </div>
    ) : error ? (
      <div style={{ height: 180, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>Error loading image</span>
      </div>
    ) : imageSrc ? (
      <Card.Img variant="top" src={imageSrc} style={{ height: 180, objectFit: 'cover', cursor: 'pointer' }} onClick={() => onPreview(obj)} />
    ) : (
      <div style={{ height: 180, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>No Preview</span>
      </div>
    );
  } else {
    // All non-image files use the new file type display
    return (
      <div 
        className={`file-thumbnail ${fileType}-thumbnail`}
        style={{ 
          height: 180, 
          background: getFileTypeDisplay.background, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          cursor: 'pointer', 
          color: getFileTypeDisplay.textColor
        }} 
        onClick={() => onPreview(obj)}
      >
        {getFileTypeDisplay.icon}
        <div style={{ fontSize: '12px', marginTop: '8px', fontWeight: 'bold' }}>
          {getFileTypeDisplay.label}
        </div>
        <div style={{ 
          fontSize: '10px', 
          opacity: 0.7, 
          textAlign: 'center', 
          padding: '0 8px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap', 
          maxWidth: '100%',
          marginTop: '4px'
        }}>
          {obj.key.length > 20 ? obj.key.substring(0, 17) + '...' : obj.key}
        </div>
      </div>
    );
  }
});

export default function Dashboard({ token, setToken }) {
  const [objects, setObjects] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalObj, setModalObj] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [renameMap, setRenameMap] = useState({});
  const [showCreateDir, setShowCreateDir] = useState(false);
  const [newDirName, setNewDirName] = useState('');

  const API_BASE = 'https://r2-object-manager.vijay-vishwakarma.workers.dev';
  
  // Memoize the fetchObjects function to prevent recreating it on every render
  const fetchObjects = useCallback(async (prefix = currentPrefix) => {
    try {
      const url = new URL(`${API_BASE}/api/list`);
      if (prefix) url.searchParams.set('prefix', prefix);
      
      const res = await axios.get(url.toString(), {
        headers: { Authorization: 'Basic ' + token }
      });
      
      if (res.data) {
        setCurrentPrefix(res.data.currentPrefix || '');
        setDirectories(res.data.directories || []);
        setFiles(res.data.files || []);
        // For backward compatibility with existing components
        setObjects([...(res.data.directories || []), ...(res.data.files || [])]);
      } else if (Array.isArray(res.data)) {
        // Fallback for old API response format
        const allObjects = res.data;
        const dirs = allObjects.filter(obj => obj.type === 'directory');
        const fileObjs = allObjects.filter(obj => obj.type !== 'directory');
        
        setCurrentPrefix('');
        setDirectories(dirs);
        setFiles(fileObjs);
        setObjects(allObjects);
      } else {
        setObjects([]);
        setDirectories([]);
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
      setObjects([]);
      setDirectories([]);
      setFiles([]);
    }
  }, [token, currentPrefix]);

  useEffect(() => { 
    fetchObjects(); 
  }, [fetchObjects]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
  }, [setToken]);

  const handleFileChange = useCallback((e) => {
    setSelectedFiles([...e.target.files]);
  }, []);

  const handleUpload = useCallback(async () => {
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    selectedFiles.forEach(f => form.append('files', f));
    if (currentPrefix) {
      form.append('prefix', currentPrefix);
    }
    try {
      await axios.post(`${API_BASE}/api/upload`, form, {
        headers: { Authorization: 'Basic ' + token },
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded * 100) / e.total));
        }
      });
      setSelectedFiles([]);
      fetchObjects();
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [selectedFiles, token, fetchObjects, currentPrefix]);

  const navigateToDirectory = useCallback((directoryKey) => {
    setCurrentPrefix(directoryKey);
    fetchObjects(directoryKey);
  }, [fetchObjects]);

  const navigateUp = useCallback(() => {
    if (currentPrefix) {
      const parts = currentPrefix.split('/').filter(Boolean);
      parts.pop(); // Remove the last directory
      const newPrefix = parts.length > 0 ? parts.join('/') + '/' : '';
      setCurrentPrefix(newPrefix);
      fetchObjects(newPrefix);
    }
  }, [currentPrefix, fetchObjects]);

  const navigateToRoot = useCallback(() => {
    setCurrentPrefix('');
    fetchObjects('');
  }, [fetchObjects]);

  const handleCreateDirectory = useCallback(async () => {
    if (!newDirName.trim()) return;
    
    try {
      await axios.post(`${API_BASE}/api/create-directory`, {
        name: newDirName.trim(),
        prefix: currentPrefix
      }, {
        headers: { Authorization: 'Basic ' + token }
      });
      
      setNewDirName('');
      setShowCreateDir(false);
      fetchObjects();
    } catch (error) {
      console.error('Create directory error:', error);
      alert('Failed to create directory. Please try again.');
    }
  }, [newDirName, currentPrefix, token, fetchObjects]);

  const handleDelete = useCallback(async (name) => {
    try {
      await axios.post(`${API_BASE}/api/delete`, { name }, {
        headers: { Authorization: 'Basic ' + token }
      });
      fetchObjects();
    } catch (error) {
      console.error('Delete error:', error);
    }
  }, [token, fetchObjects]);

  const handleRename = useCallback(async (oldName) => {
    const newName = renameMap[oldName];
    if (!newName) return;
    try {
      await axios.post(`${API_BASE}/api/rename`, { oldName, newName }, {
        headers: { Authorization: 'Basic ' + token }
      });
      setRenameMap({ ...renameMap, [oldName]: '' });
      fetchObjects();
    } catch (error) {
      console.error('Rename error:', error);
    }
  }, [token, renameMap, fetchObjects]);

  const handleDownload = useCallback(async (objectKey) => {
    try {
      const response = await axios.get(`${API_BASE}/api/preview/${encodeURIComponent(objectKey)}`, {
        headers: { Authorization: 'Basic ' + token },
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from objectKey
      const filename = objectKey.split('/').pop() || objectKey;
      link.setAttribute('download', filename);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  }, [token]);

  const openPreview = useCallback((obj) => {
    setModalObj(obj);
    setShowModal(true);
  }, []);

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col><h3>R2 Object Manager</h3></Col>
        <Col className="text-end"><Button variant="outline-danger" onClick={handleLogout}>Logout</Button></Col>
      </Row>
      
      {/* Breadcrumb Navigation */}
      <Row className="mb-3">
        <Col>
          <Breadcrumb>
            <Breadcrumb.Item 
              active={!currentPrefix} 
              onClick={!currentPrefix ? undefined : navigateToRoot}
              style={{ cursor: currentPrefix ? 'pointer' : 'default' }}
            >
              🏠 Home
            </Breadcrumb.Item>
            {currentPrefix && currentPrefix.split('/').filter(Boolean).map((part, index, arr) => {
              const isLast = index === arr.length - 1;
              const path = arr.slice(0, index + 1).join('/') + '/';
              return (
                <Breadcrumb.Item 
                  key={path}
                  active={isLast}
                  onClick={isLast ? undefined : () => {
                    setCurrentPrefix(path);
                    fetchObjects(path);
                  }}
                  style={{ cursor: isLast ? 'default' : 'pointer' }}
                >
                  📁 {part}
                </Breadcrumb.Item>
              );
            })}
          </Breadcrumb>
        </Col>
      </Row>

      {/* Upload and Create Directory Section */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Label htmlFor="file-upload">Select files to upload</Form.Label>
          <Form.Control id="file-upload" type="file" multiple onChange={handleFileChange} />
        </Col>
        <Col md={3}>
          <Button onClick={handleUpload} disabled={uploading || !selectedFiles.length} className="w-100">
            📤 Upload Files
          </Button>
        </Col>
        <Col md={3}>
          <Button variant="outline-primary" onClick={() => setShowCreateDir(true)} className="w-100">
            📁 New Folder
          </Button>
        </Col>
      </Row>
      
      {uploading && <ProgressBar now={progress} label={`${progress}%`} className="mb-3" />}
      
      {/* Objects Grid */}
      <Row xs={1} md={3} className="g-4">
        {/* Show directories first */}
        {directories.map(dir => (
          <Col key={dir.key}>
            <Card>
              <DirectoryThumbnail dir={dir} onNavigate={navigateToDirectory} />
              <Card.Body>
                <Card.Title style={{ fontSize: 14, marginBottom: '0.5rem' }} className="text-truncate" title={dir.name}>
                  📁 {dir.name}
                </Card.Title>
                <div className="text-muted small">Directory</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
        
        {/* Show files */}
        {files.map(obj => (
          <Col key={obj.key}>
            <Card>
              <ObjectThumbnail obj={obj} token={token} onPreview={openPreview} />
              <Card.Body>
                <Card.Title style={{ fontSize: 14, marginBottom: '0.5rem' }} className="text-truncate" title={obj.name || obj.key}>
                  {obj.name || obj.key}
                </Card.Title>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="btn-group btn-group-sm" role="group">
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      onClick={() => handleDownload(obj.key)}
                      title="Download"
                      className="lean-btn"
                    >
                      ⬇️
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => handleDelete(obj.key)}
                      title="Delete"
                      className="lean-btn"
                    >
                      🗑️
                    </Button>
                  </div>
                </div>
                <div className="d-flex gap-1">
                  <Form.Label htmlFor={`rename-${obj.key}`} className="visually-hidden">Rename {obj.key}</Form.Label>
                  <Form.Control 
                    id={`rename-${obj.key}`}
                    size="sm" 
                    placeholder="New name..." 
                    value={renameMap[obj.key] || ''} 
                    onChange={e => setRenameMap({ ...renameMap, [obj.key]: e.target.value })} 
                    className="flex-grow-1" 
                    style={{ fontSize: '12px' }}
                  />
                  <Button 
                    size="sm" 
                    variant="outline-secondary" 
                    onClick={() => handleRename(obj.key)}
                    disabled={!renameMap[obj.key]?.trim()}
                    title="Rename"
                    className="lean-btn"
                  >
                    ✏️
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
        
        {(!directories.length && !files.length) && (
          <Col>
            <div className="text-center text-muted py-5">
              <h5>📁 Empty Directory</h5>
              <p>No files or folders found in this location.</p>
            </div>
          </Col>
        )}
      </Row>

      {/* Create Directory Modal */}
      <Modal show={showCreateDir} onHide={() => setShowCreateDir(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Folder Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter folder name..."
              value={newDirName}
              onChange={(e) => setNewDirName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateDirectory()}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateDir(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateDirectory}
            disabled={!newDirName.trim()}
          >
            Create Folder
          </Button>
        </Modal.Footer>
      </Modal>

      {modalObj && <ObjectPreviewModal show={showModal} onHide={() => setShowModal(false)} obj={modalObj} token={token} objects={files} />}
    </Container>
  );
}
